import MaterialTable from '@material-table/core';
import { PhoneOutlined } from '@mui/icons-material';
import {
	Box,
	Card,
	CardContent,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Typography,
	useTheme
} from '@mui/material';
import { generateClient, post } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { enqueueSnackbar } from 'notistack';
import { useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from '../../../contexts/UserContext';
import { expandedContactSearch, getContact, getSegment, manualProfileByTenant } from '../../../graphql/queries';
import { cardStyle } from '../../../theme';
import { getAgentLastCall } from '../../../tools/cacheService';
import { ContactPreview } from '../contactView/contactPreview';
import { useDialTransport, WS_STATUS } from './preview/useDialTransport';

export function ManualDialing(props) {
	const theme = useTheme();
	const client = generateClient();
	const userContext = useContext(UserContext);

	const [loading, setLoading] = useState(true);
	const [manualProfiles, setManualProfiles] = useState([]);
	const [manualProfile, setManualProfile] = useState('');
	const [segment, setSegment] = useState();
	const [activeContact, setActiveContact] = useState(null);
	const [contactData, setContactData] = useState([]);
	const [lastCallData, setLastCallData] = useState(null);

	// Track whether autoSelectProfile already handled notification
	const autoSelectRanRef = useRef(false);

	// Call status state (for ContactPreview integration)
	const [callStatus, setCallStatus] = useState('');
	const [connectionLost, setConnectionLost] = useState(false);
	const activeContactRef = useRef(null);

	// Keep activeContactRef in sync
	useEffect(() => {
		activeContactRef.current = activeContact;
	}, [activeContact]);

	// --- Dial Transport (WS / IoT) ---
	const {
		subscribeToCallStatus,
		checkContact,
	} = useDialTransport({
		agentId: props.agentId,
		tenantId: userContext.tenantId,
		apiKey: userContext.apiKey,
		onCallStatusChange: (message) => {
			const status = message.callStatus || message?.value?.callStatus || '';
			setCallStatus(status);
			// Propagate active contact state to workspace level
			if (props.onActiveContactChange && activeContactRef.current) {
				const isTerminal = ['Disconnected', 'Call Ended', 'Error', 'Not Allowed To Dial'].some((t) => status?.includes(t));
				props.onActiveContactChange({
					id: activeContactRef.current.id,
					contactId: activeContactRef.current.id,
					firstName: activeContactRef.current.firstName,
					lastName: activeContactRef.current.lastName,
					phone: activeContactRef.current.phone,
					cell: activeContactRef.current.cell,
					externalId: activeContactRef.current.externalId || '',
					campaignId: activeContactRef.current.campaignId || '',
					segmentId: activeContactRef.current.segmentId || '',
					skillId: manualProfile?.skills || '',
					lastDialedNumber: activeContactRef.current.phone || activeContactRef.current.cell || '',
					hasCalledOrOnCall: true,
					isCallActive: !isTerminal,
					isLastCallContact: activeContactRef.current.isLastCallContact || false,
				});
			}
		},
		onAllowDial: null,
		onConnectionStatusChange: (status) => {
			if (status === WS_STATUS.DISCONNECTED) {
				enqueueSnackbar('Connection lost. Attempting to reconnect...', { variant: 'warning', autoHideDuration: 4000 });
				setConnectionLost(true);
			} else if (status === WS_STATUS.CONNECTED) {
				enqueueSnackbar('Connection restored', { variant: 'success', autoHideDuration: 2000 });
				setConnectionLost(false);
			}
		},
	});

	// --- Effects ---

	useEffect(() => {
		if (userContext.apiKey) {
			if (userContext.manualStrategy === 'agentLastCall') {
				autoSelectProfile();
			} else {
				getManualProfiles();
			}
		}
	}, [userContext.apiKey]);

	useEffect(() => {
		setLoading(false);
	}, [manualProfiles]);

	useEffect(() => {
		if (manualProfile) {
			if (manualProfile.manualStrategy === 'agentLastCall') {
				// If autoSelectProfile already ran and showed notifications, suppress duplicates
				const silent = autoSelectRanRef.current;
				autoSelectRanRef.current = false;
				loadLastContact({ silent });
			} else {
				getSegmentDetails();
			}
		}
	}, [manualProfile]);

	useEffect(() => {
		if (segment) {
			getContactsData();
		}
	}, [segment]);

	// --- Functions ---

	async function getSegmentDetails() {
		if (!manualProfile) return;
		try {
			const segmentResponse = await client.graphql({
				query: getSegment,
				variables: { id: manualProfile.segment },
			});
			setSegment(segmentResponse.data.getSegment);
		} catch (error) {
			console.error('Error fetching segment', error);
		}
	}

	function dayOfTheYear(date) {
		return Math.floor((date - new Date(date.getFullYear(), 0, 0)) / 1000 / 60 / 60 / 24);
	}

	async function getContactsData() {
		try {
			const fieldReplacements = ['#DOY'];
			let options = { limit: 250 };
			let nextToken = null;
			options.filter = segment.query.searchFilter;

			for (const field of fieldReplacements) {
				if (field === '#DOY') {
					options.filter = options.filter.replaceAll(field, dayOfTheYear(new Date()));
				}
			}

			if (segment.query.sort.field !== '') {
				options.sort = segment.query.sort;
			}

			const contacts = [];
			do {
				if (nextToken) options.nextToken = nextToken;
				const listData = await client.graphql({
					query: expandedContactSearch,
					variables: options,
				});
				const returnedContacts = listData.data.expandedContactSearch?.items ?? [];
				contacts.push(...returnedContacts);
				nextToken = listData.data.expandedContactSearch?.nextToken ?? null;
			} while (nextToken !== null);

			setContactData(contacts);
		} catch (error) {
			console.error(error);
		}
	}

	async function autoSelectProfile() {
		try {
			setLoading(true);
			const session = await fetchAuthSession();
			const idToken = session.tokens.idToken;

			const profilesResponse = await client.graphql({
				query: manualProfileByTenant,
				variables: {
					tenant: idToken.payload['custom:tenant'],
					limit: 1000,
				},
			});
			const returnedProfiles = profilesResponse.data.manualProfileByTenant?.items ?? [];
			const filteredProfiles = returnedProfiles.filter((p) => props.agentSkillIds.includes(p.skills));
			setManualProfiles(filteredProfiles);

			if (filteredProfiles.length === 0) {
				enqueueSnackbar('No manual dialing profiles available for your skills', { variant: 'warning' });
				setLoading(false);
				return;
			}

			let agentData = null;
			try {
				agentData = await getAgentLastCall(props.agentId, userContext.apiKey);
			} catch (e) {
				// handled by getAgentLastCall returning null
			}

			if (!agentData?.skillId) {
				enqueueSnackbar('No last call data found. Please select a profile.', { variant: 'info' });
				if (filteredProfiles.length === 1) {
					autoSelectRanRef.current = true;
					setManualProfile(filteredProfiles[0]);
				}
				setLoading(false);
				return;
			}

			const matchingProfile = filteredProfiles.find((p) => p.skills === agentData.skillId);
			if (!matchingProfile) {
				enqueueSnackbar('No matching profile for last call skill. Please select a profile.', { variant: 'info' });
				if (filteredProfiles.length === 1) {
					autoSelectRanRef.current = true;
					setManualProfile(filteredProfiles[0]);
				}
				setLoading(false);
				return;
			}

			autoSelectRanRef.current = true;
			setManualProfile(matchingProfile);
		} catch (error) {
			console.error('Error auto-selecting profile', error);
			enqueueSnackbar('Error loading last call data. Please select a profile.', { variant: 'warning' });
			setLoading(false);
		}
	}

	async function loadLastContact({ silent = false } = {}) {
		try {
			setLoading(true);

			const agentData = await getAgentLastCall(props.agentId, userContext.apiKey);

			if (!agentData?.contactId) {
				if (!silent) {
					enqueueSnackbar('No contact found for last call.', { variant: 'info' });
				}
				setLoading(false);
				return;
			}

			setLastCallData(agentData);

			const contactResponse = await client.graphql({
				query: getContact,
				variables: { id: agentData.contactId },
			});
			const contact = contactResponse.data.getContact;

			if (contact) {
				if (contact.customFields && typeof contact.customFields === 'string') {
					contact.customFields = JSON.parse(contact.customFields);
				}

				// Enrich contact with campaignId if the last call matches a callback-enabled campaign
				// the agent has an active skill for
				const matchingCampaign = (props.campaigns || []).find(
					(c) =>
						c.callbackConfigurationId &&
						c.skillId === agentData.skillId &&
						props.agentSkillIds.includes(c.skillId),
				);
				if (matchingCampaign && agentData.contactId === contact.id) {
					contact.campaignId = matchingCampaign.id;
					contact.segmentId = agentData.segmentId || contact.segmentId || '';
				}

				// Flag this contact as coming from the last-call API so the workspace
				// can gate the Schedule Callback button on manual mode appropriately.
				contact.isLastCallContact = true;

				setActiveContact(contact);
			} else {
				if (!silent) {
					enqueueSnackbar('No contact found for last call.', { variant: 'info' });
				}
			}
		} catch (error) {
			console.error('Error loading last contact', error);
			if (!silent) {
				enqueueSnackbar('Error loading last contact.', { variant: 'warning' });
			}
		} finally {
			setLoading(false);
		}
	}

	async function getManualProfiles() {
		try {
			setLoading(true);
			const idToken = (await fetchAuthSession()).tokens.idToken;

			const profilesResponse = await client.graphql({
				query: manualProfileByTenant,
				variables: {
					tenant: idToken.payload['custom:tenant'],
					limit: 1000,
				},
			});

			const returnedProfiles = profilesResponse.data.manualProfileByTenant?.items ?? [];
			const filteredProfiles = returnedProfiles.filter((profile) =>
				props.agentSkillIds.includes(profile.skills),
			);

			if (filteredProfiles.length === 0) {
				enqueueSnackbar('No manual dialing profiles available for your skills', { variant: 'warning' });
			}
			if (filteredProfiles.length === 1) {
				setManualProfile(filteredProfiles[0]);
			}
			setManualProfiles(filteredProfiles);
		} catch (error) {
			console.error(error);
		}
	}

	// Subscribe to call status when we have an active contact
	useEffect(() => {
		if (props.agentId && activeContact && manualProfile) {
			subscribeToCallStatus();
		}
	}, [props.agentId, activeContact?.id]);

	// --- Dialing handlers ---

	/**
	 * Called when the agent clicks a phone number in ContactPreview.
	 * Performs a pre-call check and then dials via the /dial REST endpoint.
	 */
	async function handleApprove(phoneNumber) {
		if (!activeContact || !manualProfile) return;

		setCallStatus('Checking...');

		// Propagate active contact to workspace level
		if (props.onActiveContactChange) {
			props.onActiveContactChange({
				id: activeContact.id,
				contactId: activeContact.id,
				firstName: activeContact.firstName,
				lastName: activeContact.lastName,
				phone: phoneNumber,
				cell: activeContact.cell,
				externalId: activeContact.externalId || '',
				campaignId: activeContact.campaignId || '',
				segmentId: activeContact.segmentId || '',
				skillId: manualProfile.skills || '',
				lastDialedNumber: phoneNumber,
				hasCalledOrOnCall: true,
				isCallActive: true,
				isLastCallContact: activeContact.isLastCallContact || false,
			});
		}

		try {
			enqueueSnackbar('Checking contact is valid for dialing', {
				variant: 'info',
				autoHideDuration: 2000,
			});

			const { passed, message } = await checkContact({
				contact: activeContact,
				phoneNumber,
				profileId: manualProfile.dialingProfile,
				suppressionProfileId: manualProfile.suppressionProfile || null,
			});

			if (!passed) {
				enqueueSnackbar(message, { variant: 'error', autoHideDuration: 3000 });
				setCallStatus('');
				return;
			}

			enqueueSnackbar(message + '. Calling...', { variant: 'success', autoHideDuration: 2000 });
			setCallStatus('Initiating Call...');

			const dialResult = await post({
				apiName: 'cdyxoutreach',
				path: '/dial',
				options: {
					headers: {
						Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
						'x-api-key': userContext.apiKey,
					},
					body: {
						contact: { ...activeContact, phone: phoneNumber },
						field: 'phone',
						dialingProfileId: manualProfile.dialingProfile,
						skillId: manualProfile.skills,
						agentId: props.agentId,
					},
				},
			}).response;

			const dialResponse = await dialResult.body.json();
			if (!dialResponse.contactId) {
				enqueueSnackbar('Error dialing contact', { variant: 'error', autoHideDuration: 3000 });
				setCallStatus('');
			}
		} catch (error) {
			console.error('Error calling contact', error);
			enqueueSnackbar('Error dialing contact', { variant: 'error', autoHideDuration: 3000 });
			setCallStatus('');
		}
	}

	/**
	 * Called when the agent dismisses the call status pane in ContactPreview.
	 */
	function handleResetApprove() {
		setCallStatus('');
	}

	function handleDismiss() {
		if (manualProfile?.manualStrategy === 'agentLastCall') {
			setActiveContact(null);
			setCallStatus('');
			if (props.onActiveContactChange) props.onActiveContactChange(null);
			loadLastContact({ silent: true });
		} else {
			setActiveContact(null);
			setCallStatus('');
			if (props.onActiveContactChange) props.onActiveContactChange(null);
		}
	}

	function handleSelectContact(rowData) {
		const contact = { ...rowData };
		if (contact.customFields && typeof contact.customFields === 'string') {
			contact.customFields = JSON.parse(contact.customFields);
		}
		delete contact?.tableData;
		setActiveContact(contact);
	}

	// --- Render ---

	return (
		<Box
			sx={{ width: '100%', backgroundColor: theme.palette.background.default, minHeight: '100%' }}
			display="flex"
			flexDirection="column"
			alignItems="center"
			gap={2}
		>
			<Box sx={{ maxWidth: '1200px', width: '100%' }} display="flex" flexDirection="column" alignItems="center" gap={2}>
				{/* Profile selector */}
				<Box sx={{ width: '100%', padding: '10px' }} display="flex" gap={2} alignItems="center">
					<FormControl fullWidth>
						<InputLabel>Manual Dialing Profile</InputLabel>
						<Select
							label="Manual Dialing Profile"
							value={manualProfile}
							onChange={(e) => setManualProfile(e.target.value)}
							disabled={userContext.manualStrategy === 'agentLastCall'}
						>
							{manualProfiles.map((profile) => (
								<MenuItem key={profile.id} value={profile}>{profile.name}</MenuItem>
							))}
						</Select>
					</FormControl>
				</Box>

				{/* Loading */}
				{loading && (
					<Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 200 }}>
						<CircularProgress />
					</Box>
				)}

				{/* Contact table (segment-based search) */}
				{!activeContact && !loading && manualProfile && segment && (
					<Box sx={{ width: '100%', padding: '10px' }} display="flex" flexDirection="column" alignItems="center">
						<Card variant="outlined" sx={cardStyle}>
							<CardContent>
								<MaterialTable
									components={{
										Container: (props) => <Box {...props} elevation={0} />,
									}}
									title={`Manual Dial Contacts for ${manualProfile.name}`}
									data={contactData}
									columns={[
										{
											title: 'External ID', field: 'externalId', width: '20%', render: (rowData) => (
												<div style={{ maxHeight: '26px', overflow: 'hidden' }}>
													{rowData.externalId}
												</div>
											),
										},
										{ title: 'First Name', field: 'firstName' },
										{ title: 'Last Name', field: 'lastName' },
										{ title: 'Source', field: 'source' },
										{ title: 'Phone', field: 'phone' },
										{ title: 'Cell', field: 'cell' },
										{ title: 'State', field: 'state' },
										{ title: 'Zip Code', field: 'zip' },
									]}
									isLoading={loading}
									options={{
										maxBodyHeight: 'calc(100vh - 350px)',
										actionsColumnIndex: -1,
										pageSize: 10,
										searchAutoFocus: true,
										searchFieldStyle: { marginBottom: '16px' },
										headerStyle: { borderTop: '1px solid #e0e0e0', padding: '16px' },
										searchFieldVariant: 'outlined',
										paginationType: 'stepped',
									}}
									actions={[
										{
											icon: () => <PhoneOutlined color="primary" />,
											onClick: (event, rowData) => handleSelectContact(rowData),
										},
									]}
								/>
							</CardContent>
						</Card>
					</Box>
				)}

				{/* No profile selected placeholder */}
				{!manualProfile && !loading && (
					<Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 200 }}>
						<Typography variant="body1" color="text.secondary">
							Select a manual dialing profile to get started
						</Typography>
					</Box>
				)}

				{/* Contact Preview */}
				{activeContact && manualProfile && (
					<Box sx={{ width: '100%' }}>
						<ContactPreview
							contact={activeContact}
							callStatus={callStatus}
							connectionLost={connectionLost}
							allowManualNumberEntry={true}
							allowMultipleCallsPerContact={true}
							allowRedial={true}
							onDismiss={handleDismiss}
							onApprove={handleApprove}
							onResetApprove={handleResetApprove}
						/>
					</Box>
				)}
			</Box>
		</Box>
	);
}
